import { useMemo } from 'react';
import {
	ENUM_UTILS,
	HIDDEN_PERP_MARKET_INDEXES,
} from '@velocity-exchange/common';
import { MarketType } from '@velocity-exchange/sdk';
import { ZERO } from 'src/constants/math';
import Env from 'src/environmentVariables/EnvironmentVariables';
import { useCurrentAccountPositions } from './query/accountTradingData/usePositions';
import { useCurrentAccountOrders } from './query/accountTradingData/useOrders';

/**
 * Returns a Set of perp market indexes that should be hidden from the current user.
 * Markets are sourced from HIDDEN_PERP_MARKET_INDEXES and the
 * NEXT_PUBLIC_HIDE_MARKET_INDEXES env flag, and are hidden unless the user has
 * active positions, orders, or unsettled PnL in that market (so they can still
 * manage existing exposure).
 */
export default function useHiddenPerpMarkets(): ReadonlySet<number> {
	const openPerpPositions = useCurrentAccountPositions();
	const openOrders = useCurrentAccountOrders();

	return useMemo(() => {
		const hiddenSet = new Set<number>();

		const candidateMarketIndexes = new Set<number>([
			...HIDDEN_PERP_MARKET_INDEXES.keys(),
			...Env.hideMarketIndexes,
		]);

		for (const marketIndex of candidateMarketIndexes) {
			const hasPositionOrUnsettledPnl = openPerpPositions?.some(
				(pos) =>
					pos.marketIndex === marketIndex &&
					(!pos.baseSize.eq(ZERO) || !pos.unsettledPnl.eq(ZERO))
			);

			const hasOrder = openOrders?.some(
				(order) =>
					order.marketIndex === marketIndex &&
					ENUM_UTILS.match(order.marketType, MarketType.PERP)
			);

			if (!hasPositionOrUnsettledPnl && !hasOrder) {
				hiddenSet.add(marketIndex);
			}
		}

		return hiddenSet;
	}, [openPerpPositions, openOrders]);
}
